import {gql} from '@apollo/client';
import {DocumentNode} from 'graphql';
import React, {useCallback, useEffect} from 'react';

import WorkspaceItemInaccessibleGuard from 'auth/WorkspaceItemInaccessibleGuard';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import ExcludeInaccessible from 'graphql-client/ExcludeInaccessible';
import {useWorksheetReducer} from 'notebook/worksheets/util/WorksheetViewStateReducer';
import {WorksheetViewActionType, WorksheetViewStateType} from 'notebook/worksheets/WorksheetConstants';
import {
  WorksheetViewQuery,
  WorksheetViewQueryVariables,
} from 'notebook/worksheets/workspace/__generated/WorksheetViewQuery';
import {logProfilerTimingEvent} from 'performance/BenchlingProfiler';
import {WORKSHEET_INITIAL_LOAD_STARTED_EVENT} from 'performance/processWorksheetsEvents';
import ProcedureWorksheetConnector, {
  WorksheetProcedureLoadingState,
} from 'procedures/worksheets/ProcedureWorksheetConnector';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';

type WorksheetOrInaccessibleGraphQL = WorksheetViewQuery['worksheet'];

type WorksheetGraphQL = ExcludeInaccessible<WorksheetOrInaccessibleGraphQL>;

export type ReviewGraphQL = NonNullable<WorksheetGraphQL['review']>;

export interface WorksheetViewProps {
  id: string;
  viewer?: boolean;
  onSetWorksheet?: (id: string) => boolean;
}

export const WORKSHEET_VIEW_QUERY: DocumentNode = gql`
  query WorksheetViewQuery($id: StrictString!) @skipAudit @engComponent(name: NOTEBOOK) {
    worksheet(id: $id) {
      ...WorksheetGraphQL
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('Worksheet').fragment}
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

/**
 * The WorksheetView orchestrates the WorksheetViewState and the WorksheetContext that are present throughout
 * the Worksheet tree. The WorksheetViewState is powered by a `useReducer` and the
 * `worksheetViewStateReducers` that orchestrate the changes that are dispatched throughout the Worksheet
 * render tree to make sure that the view is logically consistent across the entire tree. By centralizing all
 * of the State into a single place, we can ensure that any particular action that occurs updates all of the
 * appropriate context for the entire tree.
 *
 * The actions that can be dispatched are all organized under the `WorksheetViewStateAction` types.  Any node
 * within the render tree can call the `dispatch` method on the WorksheetContext to send an action that will
 * then be reduced by the `worksheetViewStateReducers` into the single context. This component will then
 * re-render the context and the tree will be updated.
 *
 * Due to this centralization, downstream components will want to make sure that they are memoizing their
 * components to just the pieces of the WorksheetContext that they care about.
 *
 * The actions that can be dispatched are:
 * - WorksheetViewLoadedStateAction -- this should be fired when the query for the Worksheet is complete
 * - WorksheetProcedureTypeLoadedStateAction-- this should be fired when a PMEI is loaded with a procedureType
 */
export default function WorksheetView({id, viewer, onSetWorksheet}: WorksheetViewProps): JSX.Element | null {
  const [worksheetViewState, dispatch] = useWorksheetReducer();
  const [loadingState, setLoadingState] = React.useState(WorksheetProcedureLoadingState.NOT_LOADED);

  const result = useBenchlingQuery<WorksheetViewQuery, WorksheetViewQueryVariables>(WORKSHEET_VIEW_QUERY, {
    variables: {id},
    onCompleted: (data) => {
      dispatch({type: WorksheetViewActionType.LOADED, worksheet: data.worksheet});
    },
  });

  const refetch = useCallback(
    async (wasServerUpdate?: boolean): Promise<void> => {
      if (wasServerUpdate && worksheetViewState.state !== WorksheetViewStateType.LOADING) {
        // Mark the worksheet as not executable while we fetch the latest server state. We only want this to be
        // blocking for server updates, not for client updates.
        dispatch({
          type: WorksheetViewActionType.RELOADING,
          worksheet: worksheetViewState.worksheet,
        });
      }
      // Fetch the latest server state
      const newResult = await result.refetch({id});
      if (newResult.data.worksheet) {
        // Mark the worksheet as loaded. This will then trigger a downstream refetch of the PMEIs to transition
        // from LOADED -> PROCEDURE_LOADED in WorksheetNavigationComponent which will then update
        // isExecutionEnabled to true
        dispatch({type: WorksheetViewActionType.LOADED, worksheet: newResult.data.worksheet});
      }
    },
    [worksheetViewState, result, id, dispatch]
  );

  useEffect(() => {
    if (
      result.type === QueryResultType.SUCCESS &&
      worksheetViewState.state !== WorksheetViewStateType.LOADING &&
      worksheetViewState.worksheet !== result.data.worksheet
    ) {
      dispatch({type: WorksheetViewActionType.LOADED, worksheet: result.data.worksheet});
    }
  }, [worksheetViewState, result, dispatch]);

  useEffect(() => {
    if (loadingState === WorksheetProcedureLoadingState.NOT_LOADED) {
      logProfilerTimingEvent(WORKSHEET_INITIAL_LOAD_STARTED_EVENT);
      setLoadingState(WorksheetProcedureLoadingState.LOADING);
    }
  }, [loadingState]);

  const {state} = worksheetViewState;
  if (state === WorksheetViewStateType.INACCESSIBLE) {
    return (
      <WorkspaceItemInaccessibleGuard
        inaccessibleInfo={worksheetViewState.worksheet}
        modelType={ModelTypeConstants.WORKSHEET}
      />
    );
  } else if (state === WorksheetViewStateType.LOADED || state === WorksheetViewStateType.RELOADING) {
    return (
      <ProcedureWorksheetConnector
        worksheet={worksheetViewState.worksheet}
        refetch={refetch}
        onSetWorksheet={onSetWorksheet}
        isExecutionEnabled={worksheetViewState.isExecutionEnabled}
        dispatch={dispatch}
        viewer={viewer}
        loadingState={loadingState}
        setLoadingState={setLoadingState}
        worksheetViewState={state}
      />
    );
  } else {
    return <CenteredHorizontalThrobber />;
  }
}
